/**
 * Asking what this user may do.
 *
 * ```tsx
 * const can = useCan();
 * if (can('media.upload', habitatId)) …
 * if (can('video.adFree')) …
 *
 * <Can entitlement="media.upload" habitat={habitatId} fallback={<Upsell />}>…</Can>
 * ```
 *
 * The answer comes from the store, so it re-renders when the session changes — a pass lapsing
 * mid-visit takes the button away at the next render rather than at the next page load, which is
 * what `hasPermission` reading `store.getState()` directly could never do.
 *
 * `can` is only ever for **Entitlements**. A **Personalisation** check — showing somebody the
 * educator app because they say they teach — shows, suggests or offers; it never asks `can`.
 */

import { createSelector } from '@reduxjs/toolkit';
import { type ComponentChildren } from 'preact';

import { type AppState, useSelector } from '@/redux/helper';

import { type Ability, createAbility, type Entitlement } from './ability';

export type { Ability, Entitlement, Verdict } from './ability';
export { createAbility } from './ability';

/**
 * The expanded ability, built once per payload rather than once per component.
 *
 * It lives here, beside the hook, so there is one memoized instance in the app: the boolean
 * selectors in `redux/selectors/users` read this rather than building their own, and `useAbility`
 * is the same object again. Returning a function from a selector is fine — redux asks its state and
 * its actions to be serializable, not its derived data — but it has to be referentially stable, and
 * `createSelector` is what makes it so.
 *
 * Prefer a boolean selector where the entitlement and id are fixed: a component reading the ability
 * re-renders whenever the payload changes, while one reading a boolean re-renders only when its own
 * answer flips.
 */
export const selectAbility = createSelector(
  (state: AppState) => state.user.subscription?.ability,
  (ability) => createAbility(ability),
);

/** The shared ability. For a check whose id varies per item — a media list, a habitat grid. */
export const useAbility = (): Ability => useSelector(selectAbility);

/** `(entitlement, id?) => boolean`. */
export const useCan = (): Ability['can'] => useAbility().can;

/** Why, not just whether — a lapsed pass offers a renewal, and one never held offers a plan. */
export const useExplain = (): Ability['explain'] => useAbility().explain;

interface CanProps {
  entitlement: Entitlement;
  /** The habitat, resource or other id this is being asked about. Omitted for unscoped ones. */
  habitat?: string;
  id?: string;
  fallback?: ComponentChildren;
  children: ComponentChildren;
}

/**
 * Renders its children when the entitlement is held, and `fallback` otherwise.
 */
export const Can = ({ entitlement, habitat, id, fallback = null, children }: CanProps) => {
  const can = useCan();
  return <>{can(entitlement, habitat ?? id) ? children : fallback}</>;
};
